import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const CheckboxDropdown = ({ label, options, value, onChange, disabled = false }) => {
  const { t } = useTranslation();
  const detailsRef = useRef(null);
  const [searchText, setSearchText] = useState('');
  // "All" is only what the summary SAYS; an empty `value` is what means it.
  const selectedLabel = !value.length
    ? t('xShell.all')
    : value.length <= 2
      ? value.join(', ')
      : `${value.slice(0, 2).join(', ')} +${value.length - 2}`;
  const normalizedSearch = searchText.trim().toLowerCase();
  const filteredOptions = normalizedSearch
    ? options.filter((option) => String(option).toLowerCase().includes(normalizedSearch))
    : options;

  const closeDropdown = () => {
    detailsRef.current?.removeAttribute('open');
    setSearchText('');
  };

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!detailsRef.current?.open) return;
      if (!detailsRef.current.contains(event.target)) {
        detailsRef.current.removeAttribute('open');
        setSearchText('');
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, []);

  const toggleValue = (option) => {
    if (disabled) return;
    if (value.includes(option)) {
      onChange(value.filter((item) => item !== option));
    } else {
      onChange([...value, option]);
    }
  };

  return (
    <div className={`srd-field srd-choice${disabled ? ' disabled' : ''}`}>
      <span>{label}</span>
      <details ref={detailsRef} onToggle={(event) => {
        if (disabled && event.currentTarget.open) event.currentTarget.removeAttribute('open');
      }}>
        <summary title={selectedLabel}>{selectedLabel}</summary>
        <div className="srd-choice-panel">
          <input
            className="srd-choice-search"
            type="search"
            value={searchText}
            disabled={disabled}
            onChange={(event) => setSearchText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') closeDropdown();
            }}
            placeholder={t('xShell.searchIn', { label })}
            autoComplete="off"
          />
          <label className="srd-choice-row">
            <input
              type="checkbox"
              checked={!value.length}
              disabled={disabled}
              onChange={() => onChange([])}
            />
            {t('xShell.all')}
          </label>
          <div className="srd-choice-list">
            {filteredOptions.map((option) => (
              <label className="srd-choice-row" key={option}>
                <input
                  type="checkbox"
                  checked={value.includes(option)}
                  disabled={disabled}
                  onChange={() => toggleValue(option)}
                />
                {option}
              </label>
            ))}
            {!filteredOptions.length && (
              <div className="srd-choice-empty">{t('xShell.noMatches')}</div>
            )}
          </div>
          <button type="button" className="srd-mini-button" onClick={closeDropdown}>
            {t('xShell.done')}
          </button>
        </div>
      </details>
    </div>
  );
};

export default CheckboxDropdown;
